import SelectUseForm from "components/SelectUseForm";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import ExportExcel from "components/excel/ExportExcel";
import { Button } from "antd";
import Widget from "components/widget/Widget";
import Loader from "components/loader";
import { IoMdDocument, IoMdSchool } from "react-icons/io";
import { MdBarChart, MdDocumentScanner } from "react-icons/md";
import { FaSchool, FaUsers, FaRobot } from "react-icons/fa";
import { IoDocumentsSharp } from "react-icons/io5";
import { useEffect, useMemo, useRef, useState } from "react";
import { estadoPublicacion } from "utils/Constants";
import usePublications from "hooks/usePublications";
import useStats from "hooks/useStats";
import NewWidget from "./NewWidget";

const DEFAULT_START_MONTH = "2023-11";

function generarRangos() {
  const nombresMeses = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  const startYear = 2023;
  const startMonth = 10;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const ranges = [];

  for (let year = startYear; year <= currentYear; year++) {
    const mesInicial = year === startYear ? startMonth : 0;
    const mesFinal = year === currentYear ? currentMonth : 11;

    for (let month = mesInicial; month <= mesFinal; month++) {
      const value = `${year}-${String(month + 1).padStart(2, "0")}`;
      const name = `${nombresMeses[month]} ${year}`;
      ranges.push({ name, value });
    }
  }

  return ranges;
}

function getMonthKey(dateValue) {
  if (!dateValue) return null;
  return String(dateValue).slice(0, 7);
}

function addCount(map, key, value = 1) {
  if (!key) return;
  map.set(key, (map.get(key) || 0) + value);
}

function createRangeCounter(monthValues, countMap) {
  // console.log(countMap)
  // console.log(monthValues, countMap)
  const prefix = [0];
  monthValues.forEach((month) => {
    prefix.push(prefix[prefix.length - 1] + (countMap.get(month) || 0));
  });
  const monthIndex = new Map(monthValues.map((month, index) => [month, index + 1]));

  return (start, end) => {
    const startIndex = monthIndex.get(start);
    const endIndex = monthIndex.get(end);

    if (!startIndex || !endIndex || startIndex > endIndex) return 0;
    return prefix[endIndex] - prefix[startIndex - 1];
  };
}

const Widgets = () => {
  const { isLoading: isLoadingUsersStats, fetchData: fetchUsersStats } = useStats("resumen-usuarios-mensual");
  const {
    isLoading: isLoadingPublications,
    fetchData: fetchPublications,
    getPostulateds,
  } = usePublications();

  const [loadingData, setLoadingData] = useState(true);
  const [currentDesde, setCurrentDesde] = useState(dayjs(new Date()).format("YYYY-MM"));
  const [currentHasta, setCurrentHasta] = useState(dayjs(new Date()).format("YYYY-MM"));
  const [dataExcel, setDataExcel] = useState([]);
  const [dataExcelAcumulada, setDataExcelAcumulada] = useState([]);
  const [visible, setVisible] = useState(false);

  const [periodoBase, setPeriodoBase] = useState(DEFAULT_START_MONTH);
  const [usersResumen, setUsersResumen] = useState({
    postulantes_activos: 0,
    instituciones_activas: 0,
    postulantes_perfil_publico: 0,
    postulantes_cv_ia: 0,
    postulantes_perfil_incompleto: 0,
  });
  const [usersMensualRaw, setUsersMensualRaw] = useState([]);

  const [totalPublicaciones, setTotalPublicaciones] = useState([]);
  const [totalPostulaciones, setTotalPostulaciones] = useState([]);

  const [totalInst, setTotalInst] = useState(0);
  const [totalInstFiltrado, setTotalInstFiltrado] = useState(0);
  const [totalPubAct, setTotalPubAct] = useState(0);
  const [totalPubALaFecha, setTotalPubALaFecha] = useState(0);
  const [insPubAct, setInsPubAct] = useState("");
  const [totPost, setTotPost] = useState(0);
  const [totPostFiltrado, setTotPostFiltrado] = useState(0);
  const [totPostPub, setTotPostPub] = useState("");
  const [totPostu, setTotPostu] = useState(0);
  const [totEspon, setTotEspon] = useState("");
  const [totNoEspon, setTotNoEspon] = useState("");
  const didInitialSearch = useRef(false);

  const {
    register,
    setValue,
    formState: { errors },
  } = useForm();

  const meses = useMemo(() => generarRangos(), []);
  const monthValues = useMemo(() => meses.map((mes) => mes.value), [meses]);
  const monthIndex = useMemo(
    () => new Map(monthValues.map((month, index) => [month, index])),
    [monthValues]
  );

  const usersMensualMap = useMemo(() => {
    const map = new Map();
    usersMensualRaw.forEach((item) => {
      if (item?.mes) map.set(item.mes, item);
    });
    return map;
  }, [usersMensualRaw]);

  const usersMensual = useMemo(() => {
    let lastPostulantesAcum = 0;
    let lastInstitucionesAcum = 0;

    return monthValues.map((mes) => {
      const source = usersMensualMap.get(mes);
      const postulantesNuevos = Number(source?.postulantes_nuevos || 0);
      const institucionesNuevas = Number(source?.instituciones_nuevas || 0);

      if (source) {
        lastPostulantesAcum = Number(source?.postulantes_acumulados || lastPostulantesAcum);
        lastInstitucionesAcum = Number(source?.instituciones_acumulados || lastInstitucionesAcum);
      } else if (mes >= periodoBase) {
        lastPostulantesAcum += postulantesNuevos;
        lastInstitucionesAcum += institucionesNuevas;
      }

      if (mes < periodoBase) {
        return {
          mes,
          postulantes_nuevos: 0,
          instituciones_nuevas: 0,
          postulantes_acumulados: 0,
          instituciones_acumuladas: 0,
        };
      }

      return {
        mes,
        postulantes_nuevos: postulantesNuevos,
        instituciones_nuevas: institucionesNuevas,
        postulantes_acumulados: lastPostulantesAcum,
        instituciones_acumuladas: lastInstitucionesAcum,
      };
    });
  }, [monthValues, usersMensualMap, periodoBase]);

  const usersMensualNormalizedMap = useMemo(() => {
    const map = new Map();
    usersMensual.forEach((item) => map.set(item.mes, item));
    return map;
  }, [usersMensual]);

  const institucionesNuevasByMonth = useMemo(() => {
    const map = new Map();
    usersMensual.forEach((item) => addCount(map, item.mes, Number(item?.instituciones_nuevas || 0)));
    return map;
  }, [usersMensual]);

  const postulantesNuevosByMonth = useMemo(() => {
    const map = new Map();
    usersMensual.forEach((item) => addCount(map, item.mes, Number(item?.postulantes_nuevos || 0)));
    return map;
  }, [usersMensual]);

  const publicacionesActivasByMonth = useMemo(() => {
    const map = new Map();
    totalPublicaciones.forEach((item) => {
      // if (item?.estado_id === estadoPublicacion.ACTIVA) {
      addCount(map, getMonthKey(item?.created_at));
      // }
    });
    return map;
  }, [totalPublicaciones]);

  const institucionesConPublicacionesByMonth = useMemo(() => {
    const map = new Map();
    totalPublicaciones.forEach((item) => {
      const month = getMonthKey(item?.created_at);
      const institutionUserId = item?.cuenta?.user_id;
      if (!month || !institutionUserId) return;
      if (!map.has(month)) map.set(month, new Set());
      map.get(month).add(institutionUserId);
    });
    return map;
  }, [totalPublicaciones]);

  const postulacionesByMonth = useMemo(() => {
    const totalMap = new Map();
    const espontaneasMap = new Map();
    const directasMap = new Map();

    totalPostulaciones.forEach((item) => {
      const month = getMonthKey(item?.created_at);
      if (!month) return;

      addCount(totalMap, month);
      if (item?.publicacion_id === null) {
        addCount(espontaneasMap, month);
      } else {
        addCount(directasMap, month);
      }
    });

    return {
      totalMap,
      espontaneasMap,
      directasMap,
    };
  }, [totalPostulaciones]);

  const countInstitucionesNuevasRange = useMemo(
    () => createRangeCounter(monthValues, institucionesNuevasByMonth),
    [monthValues, institucionesNuevasByMonth]
  );
  const countPostulantesNuevosRange = useMemo(
    () => createRangeCounter(monthValues, postulantesNuevosByMonth),
    [monthValues, postulantesNuevosByMonth]
  );
  const countPublicacionesActivasRange = useMemo(
    () => createRangeCounter(monthValues, publicacionesActivasByMonth),
    [monthValues, publicacionesActivasByMonth]
  );
  const countPostulacionesRange = useMemo(
    () => createRangeCounter(monthValues, postulacionesByMonth.totalMap),
    [monthValues, postulacionesByMonth.totalMap]
  );
  const countEspontaneasRange = useMemo(
    () => createRangeCounter(monthValues, postulacionesByMonth.espontaneasMap),
    [monthValues, postulacionesByMonth.espontaneasMap]
  );
  const countDirectasRange = useMemo(
    () => createRangeCounter(monthValues, postulacionesByMonth.directasMap),
    [monthValues, postulacionesByMonth.directasMap]
  );

  const getMonthsInRange = (start, end) => {
    const startIndex = monthIndex.get(start);
    const endIndex = monthIndex.get(end);
    if (startIndex === undefined || endIndex === undefined || startIndex > endIndex) return [];
    return monthValues.slice(startIndex, endIndex + 1);
  };

  const getInstitucionesConPublicacionesCount = (start, end) => {
    const months = getMonthsInRange(start, end);
    const institutions = new Set();
    months.forEach((month) => {
      const monthSet = institucionesConPublicacionesByMonth.get(month);
      if (!monthSet) return;
      monthSet.forEach((value) => institutions.add(value));
    });
    return institutions.size;
  };

  const getInstitucionesAcumuladasAt = (mes) =>
    Number(usersMensualNormalizedMap.get(mes)?.instituciones_acumuladas || 0);
  const getPostulantesAcumuladosAt = (mes) =>
    Number(usersMensualNormalizedMap.get(mes)?.postulantes_acumulados || 0);

  const totalPostulantes = Number(usersResumen?.postulantes_activos || 0);
  const totalPostPublicos = Number(usersResumen?.postulantes_perfil_publico || 0);
  const cvsConIA = Number(usersResumen?.postulantes_cv_ia || 0);
  const cvsIncompletos = Number(usersResumen?.postulantes_perfil_incompleto || 0);

  const profileIncompletePct = totalPostulantes
    ? ((cvsIncompletos * 100) / totalPostulantes).toFixed(2)
    : "0.00";

  const buildExportData = (data) => ({
    Periodo: `${currentDesde} - ${currentHasta}`,
    "Instituciones (acumulado)": data.Total_instituciones,
    "Instituciones (del rango)": data.Registradas,
    "Publicaciones (acumulado)": data.Publicaciones_a_la_fecha,
    "Publicaciones (del rango)": data.Publicaciones_activas,
    "Instituciones/publicaciones (del rango)": data.Instituciones_con_publicaciones_activas,
    "%Instituciones/publicaciones (del rango)":
      data.Porcentaje_Instituciones_con_publicaciones_activas,
    "Postulantes (acumulado)": data.Total_postulantes,
    "Postulantes (del rango)": data.Total_postulantes_filtrados,
    "Postulaciones totales": data.Postulaciones_totales,
    "Postulaciones espontaneas": data.Postulaciones_espontaneas,
    "Postulaciones directas": data.Postulaciones_no_espontaneas,
    "Postulantes con perfil abierto": data.Postulantes_con_perfil_abierto,
    "% Postulantes con perfil abierto": data.Porcentaje_postulantes_con_perfil_abierto,
    "Postulantes que cargaron CV con IA": data.Postulantes_con_CV_con_IA,
    "Postulantes con perfil incompleto": data.Postulantes_con_perfil_incompleto,
    "% Postulantes con perfil incompleto": data.Porcentaje_postulantes_con_perfil_incompleto,
  });

  const buildExportDataAcumulada = (data) =>
    data.map((mes) => ({
      Periodo: mes.Periodo,
      "Instituciones (acumulado)": mes.Total_instituciones,
      "Instituciones (del rango)": mes.Registradas,
      "Publicaciones (acumulado)": mes.Publicaciones_a_la_fecha,
      "Publicaciones (del rango)": mes.Publicaciones_activas,
      "Instituciones/publicaciones (del rango)": mes.Instituciones_con_publicaciones_activas,
      "%Instituciones/publicaciones (del rango)":
        mes.Porcentaje_Instituciones_con_publicaciones_activas,
      "Postulantes (acumulado)": mes.Total_postulantes,
      "Postulantes (del rango)": mes.Total_postulantes_filtrados,
      "Postulaciones totales": mes.Postulaciones_totales,
      "Postulaciones espontaneas": mes.Postulaciones_espontaneas,
      "Postulaciones directas": mes.Postulaciones_no_espontaneas,
      "Postulantes con perfil abierto": mes.Postulantes_con_perfil_abierto,
      "% Postulantes con perfil abierto": mes.Porcentaje_postulantes_con_perfil_abierto,
      "Postulantes que cargaron CV con IA": mes.Postulantes_con_CV_con_IA,
      "Postulantes con perfil incompleto": mes.Postulantes_con_perfil_incompleto,
      "% Postulantes con perfil incompleto": mes.Porcentaje_postulantes_con_perfil_incompleto,
    }));

  const loadUsersStats = async () => {
    const response = await fetchUsersStats();
    const payload = response?.resumen_actual ? response : response?.data || {};
    const resumen = payload?.resumen_actual || {};
    const mensual = Array.isArray(payload?.mensual) ? payload.mensual : [];
    const base = payload?.periodo_base || DEFAULT_START_MONTH;

    // console.log(resumen)

    setPeriodoBase(base);
    setUsersResumen({
      postulantes_activos: Number(resumen?.postulantes_activos || 0),
      instituciones_activas: Number(resumen?.instituciones_activas || 0),
      postulantes_perfil_publico: Number(resumen?.postulantes_perfil_publico || 0),
      postulantes_cv_ia: Number(resumen?.postulantes_cv_ia || 0),
      postulantes_perfil_incompleto: Number(resumen?.postulantes_perfil_incompleto || 0),
    });
    // setTotalInstFiltrado(Number(resumen?.instituciones_activas || 0));
    setUsersMensualRaw(mensual);

  };

  const fetchDataPostulations = async () => {
    const data = (await getPostulateds(null, 5, true)) || [];
    // console.log(data)
    setTotalPostulaciones(data);
  };

  const fetchGetPublications = async () => {
    const data = await fetchPublications(null, {}, true);

    // const temp = data.data.filter(p => p.activa == 1)
    // temp.forEach(t => {
    //   const date = new Date(t.created_at)
    //   if (date.getFullYear() == 2026 && date.getMonth() + 1 == 1) {
    //     // console.log(date)
    //     console.log(t)
    //   }
    // })
    // // console.log(temp[0].created_at)


    setTotalPublicaciones(data?.data || []);
  };

  const handleFilterRange = () => {
    if (currentHasta < currentDesde) {
      alert("La fecha hasta no puede ser menor a la fecha desde");
      return;
    }

    const totalInstituciones = getInstitucionesAcumuladasAt(currentHasta);
    const totalInstitucionesFiltradas = countInstitucionesNuevasRange(currentDesde, currentHasta);
    const totalPostulan = getPostulantesAcumuladosAt(currentHasta);
    const totalPostulanFiltrado = countPostulantesNuevosRange(currentDesde, currentHasta);
    const totalPublicacionesActivas = countPublicacionesActivasRange(currentDesde, currentHasta);
    // console.log(periodoBase, currentHasta)
    const totalPublicacionesALaFecha = countPublicacionesActivasRange(periodoBase, currentHasta);

    const institucionesConPubActivasCount = getInstitucionesConPublicacionesCount(
      currentDesde,
      currentHasta
    );


    // console.log(institucionesConPubActivasCount, totalInstitucionesFiltradas)
    const institucionesConPubActivasPct = totalInstitucionesFiltradas > 0 ? ((institucionesConPubActivasCount * 100) / totalInstitucionesFiltradas) : 0

    const institucionesCPublicacionesActivas = `${institucionesConPubActivasCount == 0 ? 0 : institucionesConPubActivasPct?.toFixed(2)}% (${institucionesConPubActivasCount.toLocaleString("es-AR")})`;

    const postulacionesTotales = countPostulacionesRange(currentDesde, currentHasta);
    console.log("DESDE :", currentDesde, "HASTA : ", currentHasta)
    console.log("POSTULACIONES TOTALES : ", postulacionesTotales)
    const totalEspontaneas = countEspontaneasRange(currentDesde, currentHasta);
    const totalDirectas = countDirectasRange(currentDesde, currentHasta);

    const espontaneasPct = postulacionesTotales
      ? ((totalEspontaneas * 100) / postulacionesTotales).toFixed(2)
      : "0.00";
    const directasPct = postulacionesTotales
      ? ((totalDirectas * 100) / postulacionesTotales).toFixed(2)
      : "0.00";
    const totalPostEspontaneas = `${totalEspontaneas} (${espontaneasPct}%)`;
    const totalPostNoEspontaneas = `${totalDirectas} (${directasPct}%)`;

    const totalPubsPct = totalPostulantes
      ? ((totalPostPublicos * 100) / totalPostulantes).toFixed(2)
      : "0.00";
    const totalPubs = `${totalPubsPct}% (${totalPostPublicos.toLocaleString("es-AR")})`;

    setVisible(true);
    setTotalInst(totalInstituciones);
    setTotalInstFiltrado(totalInstitucionesFiltradas);
    setTotalPubAct(totalPublicacionesActivas);
    setTotalPubALaFecha(totalPublicacionesALaFecha);
    setInsPubAct(institucionesCPublicacionesActivas);
    setTotPost(totalPostulan);
    setTotPostFiltrado(totalPostulanFiltrado);
    setTotPostPub(totalPubs);
    setTotPostu(postulacionesTotales);
    setTotEspon(totalPostEspontaneas);
    setTotNoEspon(totalPostNoEspontaneas);

    const data = {
      Total_instituciones: totalInstituciones,
      Registradas: totalInstitucionesFiltradas,
      Publicaciones_a_la_fecha: totalPublicacionesALaFecha,
      Publicaciones_activas: totalPublicacionesActivas,
      Instituciones_con_publicaciones_activas: institucionesCPublicacionesActivas,
      Porcentaje_Instituciones_con_publicaciones_activas:
        institucionesConPubActivasCount.toLocaleString("es-AR"),
      Total_postulantes: totalPostulan,
      Total_postulantes_filtrados: totalPostulanFiltrado,
      Postulaciones_totales: postulacionesTotales,
      Postulaciones_espontaneas: totalPostEspontaneas,
      Postulaciones_no_espontaneas: totalPostNoEspontaneas,
      Postulantes_con_perfil_abierto: totalPostPublicos.toLocaleString("es-AR"),
      Porcentaje_postulantes_con_perfil_abierto: `${totalPubsPct}%`,
      Postulantes_con_CV_con_IA: cvsConIA,
      Postulantes_con_perfil_incompleto: cvsIncompletos,
      Porcentaje_postulantes_con_perfil_incompleto: `${profileIncompletePct}%`,
    };

    const dataAcumulada = meses.map((mes) => {

      const instAcum = usersResumen?.instituciones_activas // getInstitucionesAcumuladasAt(mes.value);
      const instRango = countInstitucionesNuevasRange(mes.value, mes.value);
      const pubAcum = countPublicacionesActivasRange(periodoBase, mes.value);
      const pubRango = countPublicacionesActivasRange(mes.value, mes.value);
      const postAcum = usersResumen?.postulantes_activos // getPostulantesAcumuladosAt(mes.value);
      const postRango = countPostulantesNuevosRange(mes.value, mes.value);
      const postTotMes = countPostulacionesRange(mes.value, mes.value);
      const postEspMes = countEspontaneasRange(mes.value, mes.value);
      const postNoEspMes = countDirectasRange(mes.value, mes.value);

      const instConPubActMesCount = getInstitucionesConPublicacionesCount(mes.value, mes.value);
      const instConPubActMesPct = instAcum ? ((instConPubActMesCount * 100) / instAcum).toFixed(2) : "0.00";
      const postEspMesPct = postTotMes ? ((postEspMes * 100) / postTotMes).toFixed(2) : "0.00";
      const postNoEspMesPct = postTotMes ? ((postNoEspMes * 100) / postTotMes).toFixed(2) : "0.00";
      const postPubPct = totalPostulantes
        ? ((totalPostPublicos * 100) / totalPostulantes).toFixed(2)
        : "0.00";

      return {
        Periodo: mes.value,
        Total_instituciones: instAcum,
        Registradas: instRango,
        Publicaciones_a_la_fecha: pubAcum,
        Publicaciones_activas: pubRango,
        Instituciones_con_publicaciones_activas: `${instConPubActMesPct}% (${instConPubActMesCount.toLocaleString("es-AR")})`,
        Porcentaje_Instituciones_con_publicaciones_activas:
          instConPubActMesCount.toLocaleString("es-AR"),
        Total_postulantes: postAcum,
        Total_postulantes_filtrados: postRango,
        Postulaciones_totales: postTotMes,
        Postulaciones_espontaneas: `${postEspMes} (${postEspMesPct}%)`,
        Postulaciones_no_espontaneas: `${postNoEspMes} (${postNoEspMesPct}%)`,
        Postulantes_con_perfil_abierto: totalPostPublicos.toLocaleString("es-AR"),
        Porcentaje_postulantes_con_perfil_abierto: `${postPubPct}%`,
        Postulantes_con_CV_con_IA: cvsConIA,
        Postulantes_con_perfil_incompleto: cvsIncompletos,
        Porcentaje_postulantes_con_perfil_incompleto: `${profileIncompletePct}%`,
      };
    });

    setDataExcel(buildExportData(data));
    setDataExcelAcumulada(buildExportDataAcumulada(dataAcumulada));
  };

  const changeDesde = (e) => setCurrentDesde(e.target.value);
  const changeHasta = (e) => setCurrentHasta(e.target.value);

  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      await Promise.all([loadUsersStats(), fetchDataPostulations(), fetchGetPublications()]);

      setLoadingData(false);
    };

    loadData();
    setValue("desde", dayjs(new Date()).format("YYYY-MM"));
    setValue("hasta", dayjs(new Date()).format("YYYY-MM"));
  }, []);

  useEffect(() => {
    if (loadingData || didInitialSearch.current) return;

    didInitialSearch.current = true;
    handleFilterRange();
  }, [loadingData, handleFilterRange]);

  return (
    <>
      <div className="flex flex-row justify-start items-start gap-2">
        <SelectUseForm
          name="desde"
          register={register}
          onChange={changeDesde}
          errors={errors}
          className="w-[15%]"
          options={meses}
        />
        <SelectUseForm
          name="hasta"
          register={register}
          onChange={changeHasta}
          errors={errors}
          className="w-[15%]"
          options={meses}
        />

        <div className="flex flex-row justify-between items-center gap-3 mt-1">
          <Button onClick={handleFilterRange} type="primary" className="text-base primary">
            Buscar
          </Button>
          <div className={`${visible ? "d-block" : "hidden"} flex flex-row`}>
            <ExportExcel
              data={dataExcel}
              fileName={`Filtrado_${currentDesde}_${currentHasta}`}
              title={"Descargar Rango"}
            />
            <ExportExcel
              data={dataExcelAcumulada}
              fileName={"Consolidado_anual"}
              title={"Descargar Acumulado"}
            />
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4 lg:grid-cols-4 2xl:grid-cols-4 3xl:grid-cols-4">

        {/* <NewWidget icon={<FaSchool className="h-4 w-4 fill-main" />} isLoading={isLoadingUsersStats} title="INSTITUCIONES" value={usersResumen?.instituciones_activas} />
        <NewWidget icon={<IoMdSchool className="h-4 w-4 fill-main" />} isLoading={isLoadingUsersStats} title="POSTULANTES" value={usersResumen?.postulantes_activos} />
        <NewWidget icon={<IoMdDocument className="h-4 w-4 fill-main" />} isLoading={isLoadingPublications} title="PUBLICACIONES" value={totalPubALaFecha} />

        <NewWidget icon={<FaRobot className="h-4 w-4 fill-main" />} isLoading={isLoadingUsersStats} title="CV's cargados con IA" value={cvsConIA} />
        <NewWidget icon={<MdDocumentScanner className="h-4 w-4 fill-main" />} isLoading={isLoadingUsersStats} title="Perfiles incompletos" value={cvsIncompletos} subValue={`(${profileIncompletePct}%)`} /> */}

        <Widget
          icon={<FaSchool className="h-6 w-6" />}
          title={"Instituciones"}
          subtitle={isLoadingUsersStats ? <Loader small={true} /> : usersResumen?.instituciones_activas}
        />

        <Widget
          icon={<IoMdSchool className="h-7 w-7" />}
          title={"Postulantes"}
          subtitle={loadingData ? <Loader small={true} /> : totPost}
        />

        <Widget
          icon={<IoMdDocument className="h-6 w-6" />}
          title={"Publicaciones"}
          subtitle={isLoadingPublications ? <Loader small={true} /> : totalPubALaFecha}
        />

        <Widget
          icon={<FaRobot className="h-6 w-6" />}
          title={"CV's cargados con IA"}
          subtitle={isLoadingUsersStats ? <Loader small={true} /> : cvsConIA}
        />



        <Widget
          icon={<FaSchool className="h-6 w-6" />}
          title={"Instituciones (rango)"}
          subtitle={isLoadingUsersStats ? <Loader small={true} /> : totalInstFiltrado}
        />

        <Widget
          icon={<IoMdSchool className="h-7 w-7" />}
          title={"Postulantes (rango)"}
          subtitle={loadingData ? <Loader small={true} /> : totPostFiltrado}
        />


        <Widget
          icon={<IoMdDocument className="h-6 w-6" />}
          title={"Publicaciones (rango)"}
          subtitle={isLoadingPublications ? <Loader small={true} /> : totalPubAct}
        />

        <Widget
          icon={<MdDocumentScanner className="h-6 w-6" />}
          title={"Perfiles incompletos"}
          subtitle={
            isLoadingUsersStats ? (
              <Loader small={true} />
            ) : (
              `${cvsIncompletos} (${profileIncompletePct}%)`
            )
          }
        />

        <Widget
          icon={<MdBarChart className="h-7 w-7" />}
          title={"% Instituciones/publicaciones (rango)"}
          subtitle={isLoadingPublications ? <Loader small={true} /> : insPubAct}
        />




        <Widget
          icon={<FaUsers className="h-6 w-6" />}
          title={"% Perfiles públicos"}
          subtitle={loadingData ? <Loader small={true} /> : totPostPub}
        />



        <Widget
          icon={<IoDocumentsSharp className="h-7 w-7" />}
          title={"Postulaciones totales"}
          subtitle={loadingData ? <Loader small={true} /> : totPostu}
          secondTitle={"Espontáneas"}
          secondSubtitle={loadingData ? <Loader small={true} /> : totEspon}
          thirdTitle={"Directas"}
          thirdSubtitle={loadingData ? <Loader small={true} /> : totNoEspon}
        />
      </div>
    </>
  );
};

export default Widgets;
